import { generateClient } from 'aws-amplify/data';
import { getCurrentUser } from 'aws-amplify/auth';

const VALID_ACTIONS = [
  'NEWSLETTER_GENERATED', 'NEWSLETTER_EDITED', 'NEWSLETTER_REVIEWED',
  'NEWSLETTER_SENT', 'NEWSLETTER_ARCHIVED', 'DISCLAIMER_UPDATED',
  'CLIENT_DATA_EXPORTED',
  // #434 — advisor sent themselves a rendered test copy (never a distribution)
  'TEST_SEND',
  // Internal aliases (backward compat with service integrations)
  // Phase 1C (#169): added 'REJECT' for compliance approval flow
  'GENERATE', 'EDIT', 'APPROVE', 'REJECT', 'SEND', 'DELETE', 'SCHEDULE', 'CANCEL',
];

const VALID_RESOURCE_TYPES = ['NEWSLETTER', 'CAMPAIGN', 'TEMPLATE', 'DISCLAIMER', 'CLIENT_DATA'];

class AuditService {
  constructor() {
    try {
      this.client = generateClient();
    } catch {
      this.client = null;
    }
  }

  async generateContentHash(content) {
    if (!content) return '';
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async getCurrentUserId() {
    try {
      const user = await getCurrentUser();
      return user.userId;
    } catch {
      return 'anonymous';
    }
  }

  async logAction({ action, resourceType, resourceId, content, details, previousContentHash, metadata }) {
    if (!VALID_ACTIONS.includes(action)) {
      throw new Error(`Invalid audit action: ${action}. Must be one of: ${VALID_ACTIONS.join(', ')}`);
    }
    if (!VALID_RESOURCE_TYPES.includes(resourceType)) {
      throw new Error(`Invalid resource type: ${resourceType}. Must be one of: ${VALID_RESOURCE_TYPES.join(', ')}`);
    }

    try {
      const userId = await this.getCurrentUserId();
      const contentHash = content ? await this.generateContentHash(content) : '';
      const timestamp = new Date().toISOString();

      // Capture browser context for compliance
      const ipAddress = ''; // Captured server-side via Lambda/API Gateway
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';

      const entry = {
        userId,
        action,
        resourceType,
        resourceId,
        timestamp,
        contentHash,
        ipAddress,
        userAgent,
        ...(details && { details: JSON.stringify(details) }),
        ...(previousContentHash && { previousContentHash }),
        ...(metadata && { metadata: JSON.stringify(metadata) }),
      };

      const { data, errors } = await this.client.models.ComplianceAuditLog.create(entry);
      if (errors?.length) {
        console.error('[AuditService] GraphQL errors on create:', JSON.stringify(errors));
      } else {
        console.log('[AuditService] Audit log created:', data?.id, action, resourceType);
      }
      return data;
    } catch (error) {
      console.error('[AuditService] Failed to create audit log:', error?.message || error);
      return null;
    }
  }

  async getAuditLogs({ resourceId, userId, actionType, startDate, endDate, limit = 50 } = {}) {
    try {
      const filter = {};
      if (resourceId) filter.resourceId = { eq: resourceId };
      if (userId) filter.userId = { eq: userId };
      if (actionType) filter.action = { eq: actionType };
      if (startDate) filter.timestamp = { ...filter.timestamp, ge: startDate };
      if (endDate) filter.timestamp = { ...filter.timestamp, le: endDate };

      const { data, errors } = await this.client.models.ComplianceAuditLog.list({
        filter,
        limit,
      });

      if (errors?.length) {
        console.error('[AuditService] GraphQL errors on list:', JSON.stringify(errors));
      }
      console.log('[AuditService] Fetched audit logs:', data?.length || 0, 'entries');
      return data || [];
    } catch (error) {
      console.error('[AuditService] Failed to fetch audit logs:', error?.message || error);
      return [];
    }
  }

  async getResourceHistory(resourceId) {
    return this.getAuditLogs({ resourceId, limit: 100 });
  }

  exportToCSV(logs) {
    const headers = ['Timestamp', 'Action', 'Resource Type', 'Resource ID', 'User ID', 'Content Hash', 'Details', 'IP Address', 'User Agent'];
    const rows = logs.map(log => {
      const details = log.details ? (typeof log.details === 'string' ? log.details : JSON.stringify(log.details)) : '';
      return [
        log.timestamp, log.action, log.resourceType, log.resourceId,
        log.userId, log.contentHash || '', details.replace(/"/g, '""'),
        log.ipAddress || '', log.userAgent || '',
      ].map(v => `"${v}"`).join(',');
    });
    return [headers.join(','), ...rows].join('\n');
  }

  exportToJSON(logs) {
    return JSON.stringify(logs, null, 2);
  }
}

AuditService.VALID_ACTIONS = VALID_ACTIONS;
AuditService.VALID_RESOURCE_TYPES = VALID_RESOURCE_TYPES;

const auditServiceInstance = new AuditService();
export default auditServiceInstance;
export { AuditService };
